import apiClient from "./apiClient";
import { normalizeApiError } from "../utils/apiError";

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function extractData(payload) {
  return payload?.data ?? payload;
}

const stockProcessingService = {
  async getStockProcessingDetails(params = {}) {
    try {
      // Remove empty values to avoid sending unusable filters.
      // Specifically: yardId, lotId, blContainerId, stockCategoryId
      const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});

      const response = await apiClient.get("/stock-processing", {
        params: cleanParams,
      });

      const payload = response?.data || {};
      const dataRoot = extractData(payload);
      const items = toArray(dataRoot?.data || dataRoot?.items || dataRoot);
      const meta = dataRoot?.meta || payload?.meta || {};

      return {
        items,
        meta: {
          page: Number(meta?.page || cleanParams.page || 1),
          limit: Number(meta?.limit || cleanParams.limit || 10),
          total: Number(meta?.total || meta?.totalItems || items.length || 0),
          totalPages: Number(meta?.totalPages || 1),
        },
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getStockProcessingById(id) {
    if (!id) throw new Error("ID is required");
    try {
      const response = await apiClient.get(`/stock-processing/${id}`);
      return extractData(response?.data || {});
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async createStockProcessing(payload) {
    try {
      const response = await apiClient.post("/stock-processing", payload);
      return extractData(response?.data || {});
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async bulkCreateStockProcessing(payload) {
    try {
      const response = await apiClient.post("/stock-processing/bulk", payload);
      return extractData(response?.data || {});
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async bulkProcessing(payload) {
    try {
      const response = await apiClient.post("/stock-processing/bulk-processing", payload);
      return extractData(response?.data || {});
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async updateBulkStockProcessing(payload) {
    try {
      const response = await apiClient.put("/stock-processing/bulk", payload);
      return extractData(response?.data || {});
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async deleteBulkStockProcessing(lotId, date) {
    if (!lotId || !date) throw new Error("Lot ID and Date are required");
    try {
      const response = await apiClient.delete("/stock-processing/bulk", {
        params: { lotId, date },
      });
      return extractData(response?.data || {});
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async deleteStockProcessingById(id) {
    if (!id) throw new Error("Stock Processing ID is required");
    try {
      const response = await apiClient.delete("/stock-processing/bulk", {
        params: { stockProcessingId: id },
      });
      return extractData(response?.data || {});
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async lockLot(lotId) {
    if (!lotId) throw new Error("Lot ID is required");
    try {
      const response = await apiClient.post("/stock-processing/lock-lot", { lotId });
      return extractData(response?.data || {});
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async downloadLotwiseExcelReport(lotId) {
    if (!lotId) throw new Error("Lot ID is required");
    try {
      const response = await apiClient.get("/stock-processing/lotwise-excel-report", {
        params: { lotId },
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getStockProcessingDetail(params = {}) {
    try {
      const response = await apiClient.get("/stock-processing/detail", { params });
      return extractData(response?.data || {});
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getStockHistory(params = {}) {
    try {
      const response = await apiClient.get("/stock-processing/history", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);
      return {
        items: toArray(dataRoot?.data || dataRoot?.items || dataRoot),
        meta: dataRoot?.meta || payload?.meta || {},
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getLotwiseStock(params = {}) {
    try {
      const response = await apiClient.get("/stock-processing/lotwise", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);
      return {
        items: toArray(dataRoot?.data || dataRoot?.items || dataRoot),
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getTransferLotwiseStockCategory(params = {}) {
    try {
      const response = await apiClient.get("/stock-processing/transfer-lotwise-stock-category", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);
      return {
        items: toArray(dataRoot?.data || dataRoot?.items || dataRoot),
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async transferStock(payload) {
    try {
      const response = await apiClient.post("/stock-processing/transfer", payload);
      return extractData(response?.data || {});
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async revertStockTransfer(transferMasterId) {
    if (!transferMasterId) throw new Error("Transfer Master ID is required");
    try {
      const response = await apiClient.delete("/stock-processing/transfer", {
        params: { transferMasterId },
      });
      return extractData(response?.data || {});
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getStockTransfers(params = {}) {
    try {
      const response = await apiClient.get("/stock-processing/transfer", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);
      return {
        items: toArray(dataRoot?.data || dataRoot?.items || dataRoot),
        meta: dataRoot?.meta || payload?.meta || {},
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getStockTransferHistory(params = {}) {
    try {
      const response = await apiClient.get("/stock-processing/transfer-by-lot", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);
      return {
        items: toArray(dataRoot?.data || dataRoot?.items || dataRoot),
        meta: dataRoot?.meta || payload?.meta || {},
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getActiveBLContainerStockDetails() {
    try {
      const response = await apiClient.get("/container-stock-details/active-listing");
      const payload = response?.data || {};
      const dataRoot = extractData(payload);
      return {
        items: toArray(dataRoot?.data || dataRoot?.items || dataRoot),
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getBLContainerStockDetailById(id) {
    if (!id) throw new Error("ID is required");
    try {
      const response = await apiClient.get(`/container-stock-details/${id}`);
      const payload = response?.data || {};
      return extractData(payload);
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async lockBLContainerStock(id) {
    if (!id) throw new Error("ID is required");
    try {
      const response = await apiClient.patch(`/container-stock-details/lock/${id}`);
      return extractData(response?.data || {});
    } catch (error) {
      throw normalizeApiError(error);
    }
  },
};

export default stockProcessingService;
